// Briques communes aux écrans de service : socket temps réel, libellés,
// helpers de formatage, garde d'accès (session du restaurateur).

import React, { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { SOCKET_URL, getAdminToken, clearAdminToken } from '../utils/api';
import { staffFetch } from '../utils/staffApi';
import AdminLogin from '../AdminLogin';
import './StaffShared.css';

export const PAYMENT_METHODS = [
  { key: 'cb', label: 'Carte bancaire', emoji: '💳' },
  { key: 'especes', label: 'Espèces', emoji: '💶' },
  { key: 'cheque', label: 'Chèque', emoji: '🖊️' },
  { key: 'cheques_vacances', label: 'Chèques vacances', emoji: '🏖️' },
  { key: 'mixte', label: 'Mixte', emoji: '➗' }
];

export const PAYMENT_LABELS = Object.fromEntries(PAYMENT_METHODS.map(m => [m.key, m.label]));

export const ORDER_STATUS = {
  pending: { label: 'Nouveau', class: 'st-pending', next: 'preparing', nextLabel: 'Commencer' },
  preparing: { label: 'En préparation', class: 'st-preparing', next: 'ready', nextLabel: 'Prêt !' },
  ready: { label: 'Prêt', class: 'st-ready', next: 'completed', nextLabel: 'Servi / Retiré' },
  completed: { label: 'Terminé', class: 'st-completed' },
  cancelled: { label: 'Annulé', class: 'st-cancelled' }
};

export const euro = (n) => `${(Math.round(parseFloat(n || 0) * 100) / 100).toFixed(2)}€`;

export const timeHM = (value) =>
  new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit'
  }).format(new Date(value));

export const minutesSince = (value) =>
  Math.max(0, Math.round((Date.now() - new Date(value).getTime()) / 60000));

export const ago = (value) => {
  const m = minutesSince(value);
  if (m < 1) return "à l'instant";
  if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60);
  return `il y a ${h}h${String(m % 60).padStart(2, '0')}`;
};

// Étiquette d'une commande pour les écrans de service
export const orderWho = (order) => {
  if (order.channel === 'dine_in') {
    const table = order.tableName ? `Table ${order.tableName}` : 'Sur place';
    return order.guestLabel ? `${table} · ${order.guestLabel}` : table;
  }
  return order.customerName;
};

// Re-render périodique pour les compteurs "il y a X min"
export const useTicker = (ms = 30000) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(v => v + 1), ms);
    return () => clearInterval(t);
  }, [ms]);
};

// Connexion socket partagée : reconnecte seule, renvoie l'état de connexion
export const useServiceSocket = (handlers) => {
  const [connected, setConnected] = useState(false);
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    const socket = io(SOCKET_URL, { transports: ['websocket', 'polling'] });
    socket.on('connect', () => setConnected(true));
    socket.on('disconnect', () => setConnected(false));
    const events = ['new-order', 'order-updated', 'tab-updated', 'products-updated'];
    events.forEach(evt => {
      socket.on(evt, (payload) => {
        const fn = handlersRef.current && handlersRef.current[evt];
        if (fn) fn(payload);
      });
    });
    return () => socket.disconnect();
  }, []);

  return connected;
};

// Bip discret joué à l'arrivée d'une commande (WebAudio, aucun fichier)
let audioCtx = null;
export const playDing = () => {
  try {
    audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    if (audioCtx.state === 'suspended') audioCtx.resume();
    const play = (freq, start, dur) => {
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, audioCtx.currentTime + start);
      gain.gain.exponentialRampToValueAtTime(0.4, audioCtx.currentTime + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, audioCtx.currentTime + start + dur);
      osc.connect(gain).connect(audioCtx.destination);
      osc.start(audioCtx.currentTime + start);
      osc.stop(audioCtx.currentTime + start + dur + 0.05);
    };
    play(880, 0, 0.35);
    play(1174, 0.18, 0.45);
  } catch {
    // pas de son disponible : tant pis
  }
};

// --- Écran de connexion PIN ---

// Garde d'accès : les écrans de service utilisent la session du restaurateur.
// On connecte la tablette une fois avec le mot de passe admin ; elle reste
// connectée (30 jours). « Quitter » déconnecte la tablette.
export function StaffGate({ children }) {
  const [authed, setAuthed] = useState(() => Boolean(getAdminToken()));

  if (!authed) {
    return <AdminLogin onLogin={() => setAuthed(true)} />;
  }

  const logout = () => { clearAdminToken(); setAuthed(false); };
  return children(null, logout);
}

// --- Modale d'encaissement ---

export function PayModal({ tab, onClose, onPaid }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const pay = async (method) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const res = await staffFetch(`/service/tab/${tab.id}/close`, {
        method: 'POST',
        body: JSON.stringify({ paymentMethod: method })
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(body.message || 'Encaissement impossible');
        return;
      }
      onPaid(body);
    } catch {
      setError('Erreur réseau');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="staff-modal-overlay" onClick={onClose}>
      <div className="staff-modal" onClick={(e) => e.stopPropagation()}>
        <h2>Encaisser la table {tab.tableName}</h2>
        <div className="pay-total">{euro(tab.total)}</div>
        {tab.redeemedPoints > 0 && (
          <p className="pay-points">dont articles offerts contre {tab.redeemedPoints} pts fidélité</p>
        )}
        {error && <p className="staff-error">{error}</p>}
        <div className="pay-methods">
          {PAYMENT_METHODS.map(m => (
            <button key={m.key} className="pay-method" disabled={busy} onClick={() => pay(m.key)}>
              <span className="pay-method-emoji">{m.emoji}</span>
              {m.label}
            </button>
          ))}
        </div>
        <button className="staff-btn-ghost" onClick={onClose}>Annuler</button>
      </div>
    </div>
  );
}
